import { useTranslation } from "react-i18next";
import { CreateHomeworkAssignmentRequestBody } from "@packages/api/madlenAPI.schemas";
import { useEffect, useState, useRef } from "react";
import { Button, Text } from "@core/index";
import { Input } from "@core/input";
import { DateTimePicker } from "@core/datepicker";
import { IconClipboardText, IconListCheck } from "@tabler/icons-react";

// Extended type to include custom properties needed for the component
interface ExtendedAssignmentRequestBody
  extends CreateHomeworkAssignmentRequestBody {
  hwAssignmentType?: "HOMEWORK" | "QUIZ";
  duration?: number;
}

const CommonPropertiesStep = ({
  assignmentData,
  onChange,
}: {
  assignmentData: ExtendedAssignmentRequestBody;
  onChange: (data: Partial<ExtendedAssignmentRequestBody>) => void;
}) => {
  const { t } = useTranslation();
  const [localAssignmentData, setLocalAssignmentData] =
    useState<ExtendedAssignmentRequestBody>(assignmentData);

  const [durationString, setDurationString] = useState(
    assignmentData.duration?.toString() ?? "",
  );

  // Ref to track if changes are from props or user input
  const isUpdatingFromProps = useRef(false);

  // Sync local state with prop changes
  useEffect(() => {
    isUpdatingFromProps.current = true;
    setLocalAssignmentData(assignmentData);
    setDurationString(assignmentData.duration?.toString() ?? "");

    setTimeout(() => {
      isUpdatingFromProps.current = false;
    }, 0);
  }, [assignmentData]);

  // Update parent when local state changes (but only for user input changes)
  useEffect(() => {
    if (!isUpdatingFromProps.current) {
      onChange(localAssignmentData);
    }
  }, [localAssignmentData, onChange]);

  const handleDurationChange = (value: string) => {
    setDurationString(value);

    if (!value) {
      setLocalAssignmentData({
        ...localAssignmentData,
        duration: 0,
        endDate: localAssignmentData.startDate,
      });
      return;
    }

    const parsedDuration = parseInt(value, 10);

    if (isNaN(parsedDuration)) {
      return;
    }

    setLocalAssignmentData({
      ...localAssignmentData,
      duration: parsedDuration,
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-2">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.properties.name")}
          </label>
          <Input
            type="text"
            value={localAssignmentData.name}
            data-testid="assignment-name-input"
            onChange={(e) =>
              setLocalAssignmentData({
                ...localAssignmentData,
                name: e.target.value,
              })
            }
            classNames={{
              input:
                "w-full p-2 border rounded-md bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 border-dark-300 dark:border-dark-400",
            }}
            placeholder={t("assignments.generator.properties.enterName")}
          />
        </div>
        {/* Type */}
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1 mt-2">
          {t("assignments.generator.properties.type")}
        </label>
        <div className="grid grid-cols-1 gap-2 border border-gray-200 dark:border-dark-400 rounded-xl">
          <div className="flex w-full">
            <Button
              variant="outline"
              textColor={`${
                localAssignmentData.hwAssignmentType === "HOMEWORK" &&
                "text-primary-500 dark:text-white"
              }`}
              fullWidth
              className={`p-3 rounded-tl-xl col-span-4 ${
                localAssignmentData.hwAssignmentType === "HOMEWORK"
                  ? "border-dark-500"
                  : "border-gray-200 dark:border-dark-500 hover:border-dark-500 dark:hover:border-dark-500"
              }`}
              bgColor={`${
                localAssignmentData.hwAssignmentType === "HOMEWORK" &&
                "bg-indigo-50 dark:bg-primary-500"
              }`}
              size="sm"
              leftIcon={<IconClipboardText size={20} className="mr-2" />}
              onClick={() => {
                setLocalAssignmentData({
                  ...localAssignmentData,
                  hwAssignmentType: "HOMEWORK",
                  endDate: new Date(
                    new Date().getTime() + 2 * 24 * 60 * 60 * 1000,
                  ).toISOString(),
                });
              }}
            >
              {t("HOMEWORK")}
            </Button>
            <Button
              variant="outline"
              textColor={`${
                localAssignmentData.hwAssignmentType === "QUIZ" &&
                "text-primary-500 dark:text-white"
              }`}
              fullWidth
              className={`p-3 rounded-tr-xl col-span-4 ${
                localAssignmentData.hwAssignmentType === "QUIZ"
                  ? "border-dark-500"
                  : "border-gray-200 dark:border-dark-500 hover:border-dark-500 dark:hover:border-dark-500"
              }`}
              bgColor={`${
                localAssignmentData.hwAssignmentType === "QUIZ" &&
                "bg-indigo-50 dark:bg-primary-500"
              }`}
              size="sm"
              leftIcon={<IconListCheck size={20} className="mr-2" />}
              onClick={() => {
                setLocalAssignmentData({
                  ...localAssignmentData,
                  hwAssignmentType: "QUIZ",
                  endDate: undefined,
                });
              }}
            >
              {t("QUIZ")}
            </Button>
          </div>

          {localAssignmentData.hwAssignmentType === "HOMEWORK" ? (
            <div className="p-4 pt-0">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t("assignments.generator.properties.endDate")}
              </label>
              <DateTimePicker
                value={localAssignmentData.endDate || ""}
                onChange={(date) =>
                  setLocalAssignmentData({
                    ...localAssignmentData,
                    endDate: date,
                  })
                }
                minDate={new Date()}
                className="w-full"
                placeholder={t(
                  "assignments.generator.properties.selectEndDate",
                )}
              />
            </div>
          ) : (
            <div className="p-4 pt-0">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                {t("assignments.generator.properties.duration")}{" "}
                {"(" + t("common.time.minutes") + ")"}
              </label>
              <Input
                type="text"
                value={durationString}
                onChange={(e) => handleDurationChange(e.target.value)}
                classNames={{
                  input:
                    "w-full p-2 border rounded-md bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600",
                }}
                placeholder={t(
                  "assignments.generator.properties.enterDuration",
                )}
              />
              {isNaN(parseInt(durationString, 10)) && (
                <Text variant="p" className="text-xs mt-1 text-red-500">
                  {t("assignments.generator.properties.invalidDuration")}
                </Text>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommonPropertiesStep;
